import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate, useNavigate } from "react-router-dom";
import { PageHead } from "@/pages/AppLayout";
import { API } from "@/lib/api";
import { CheckCircle2, XCircle, AlertTriangle, Clock, ShieldCheck, FileDown, ChevronRight, ChevronLeft } from "lucide-react";

function Stat({ label, value, accent }) {
  return (
    <div className="card p-5 border-t-4" style={{ borderTopColor: accent }} data-testid={`compliance-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <p className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8]">{label}</p>
      <p className="font-mono text-4xl mt-2" style={{ color: accent }}>{value}</p>
    </div>
  );
}

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default function Compliance() {
  const { isManager } = useAuth();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [date, setDate] = useState(todayStr());

  useEffect(() => { if (isManager) { setD(null); api.get(`/compliance?date=${date}`).then(({ data }) => setD(data)); } }, [date, isManager]);

  if (!isManager) return <Navigate to="/dashboard" replace />;

  const shiftDay = (delta) => {
    const dt = new Date(date + "T00:00:00");
    dt.setDate(dt.getDate() + delta);
    const next = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
    if (next <= todayStr()) setDate(next);
  };
  const isToday = date >= todayStr();

  const datePicker = (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="flex items-center gap-1">
        <button type="button" onClick={() => shiftDay(-1)} className="h-11 w-11 flex items-center justify-center border border-white/15 text-white hover:border-[#FFB800] hover:text-[#FFB800] transition-colors" data-testid="compliance-prev-day" aria-label="Previous day"><ChevronLeft className="w-5 h-5" /></button>
        <input type="date" value={date} max={todayStr()} onChange={(e) => setDate(e.target.value)}
          className="il-input font-mono h-11 w-auto" data-testid="compliance-date" />
        <button type="button" onClick={() => shiftDay(1)} disabled={isToday} className="h-11 w-11 flex items-center justify-center border border-white/15 text-white hover:border-[#FFB800] hover:text-[#FFB800] transition-colors disabled:opacity-30 disabled:hover:border-white/15 disabled:hover:text-white" data-testid="compliance-next-day" aria-label="Next day"><ChevronRight className="w-5 h-5" /></button>
      </div>
      {!isToday && <button type="button" onClick={() => setDate(todayStr())} className="h-11 px-3 border border-white/15 text-[#94A3B8] font-head text-xs uppercase tracking-wide hover:text-white transition-colors" data-testid="compliance-today">Today</button>}
      <a href={`${API}/compliance/report.pdf?date=${date}&token=${localStorage.getItem("ironlog_token")}`}
        target="_blank" rel="noopener noreferrer"
        className="flex items-center gap-2 px-3 h-11 bg-[#FFB800] text-black font-head text-xs uppercase tracking-wide hover:brightness-110 transition-all whitespace-nowrap"
        data-testid="compliance-pdf-btn">
        <FileDown className="w-4 h-4" /> Day PDF
      </a>
      <a href={`${API}/compliance/week.pdf?date=${date}&token=${localStorage.getItem("ironlog_token")}`}
        target="_blank" rel="noopener noreferrer"
        className="flex items-center gap-2 px-3 h-11 border border-white/15 text-white font-head text-xs uppercase tracking-wide hover:border-[#FFB800] hover:text-[#FFB800] transition-all whitespace-nowrap"
        data-testid="compliance-week-pdf-btn">
        <FileDown className="w-4 h-4" /> Week PDF
      </a>
    </div>
  );

  if (!d) return (
    <div className="p-5 md:p-10" data-testid="compliance-page">
      <PageHead title="Compliance" sub="Daily pre-start checks across your fleet.">{datePicker}</PageHead>
      <p className="font-head text-2xl text-[#FFB800] uppercase tracking-widest">Loading…</p>
    </div>
  );

  const pct = d.summary.total ? Math.round((d.summary.checked / d.summary.total) * 100) : 0;

  const statusCell = (m) => {
    if (!m.checked) return <span className="inline-flex items-center gap-1.5 font-mono text-xs text-[#94A3B8]"><XCircle className="w-4 h-4" /> Not checked</span>;
    if (m.status === "fail") return <span className="inline-flex items-center gap-1.5 font-mono text-xs text-[#FF3B30]"><AlertTriangle className="w-4 h-4" /> Fault</span>;
    return <span className="inline-flex items-center gap-1.5 font-mono text-xs text-[#22C55E]"><CheckCircle2 className="w-4 h-4" /> Passed</span>;
  };

  return (
    <div className="p-5 md:p-10 pb-24" data-testid="compliance-page">
      <PageHead title="Compliance" sub={`Pre-start checks for ${d.is_today ? "today" : d.date}.`}>{datePicker}</PageHead>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Stat label="Machines" value={d.summary.total} accent="#FFB800" />
        <Stat label="Checked" value={`${d.summary.checked}/${d.summary.total}`} accent="#22C55E" />
        <Stat label="Not Checked" value={d.summary.not_checked} accent="#FF3B30" />
        <Stat label="Faults" value={d.summary.faults} accent="#FF3B30" />
      </div>

      <div className="card p-5 mb-6" data-testid="compliance-rate">
        <div className="flex items-center justify-between mb-2">
          <p className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8]">Compliance Rate</p>
          <p className="font-mono text-lg" style={{ color: pct === 100 ? "#22C55E" : pct >= 50 ? "#FFB800" : "#FF3B30" }}>{pct}%</p>
        </div>
        <div className="h-2.5 bg-white/5">
          <div className="h-2.5 transition-all" style={{ width: `${pct}%`, background: pct === 100 ? "#22C55E" : pct >= 50 ? "#FFB800" : "#FF3B30" }} />
        </div>
      </div>

      <div className="card" data-testid="compliance-table">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-white/10">
          <ShieldCheck className="w-5 h-5 text-[#FFB800]" />
          <h2 className="font-head font-700 text-xl uppercase tracking-wide">Machine Checks</h2>
        </div>
        {d.machines.length === 0 ? <p className="p-5 font-body text-[#94A3B8] text-sm">No machines in your fleet yet.</p> : (
          <div className="divide-y divide-white/10">
            {d.machines.map((m) => (
              <button key={m.id} onClick={() => nav(`/prestart?machine=${m.id}`)} type="button"
                className={`w-full text-left flex items-center gap-4 px-5 py-3.5 hover:bg-[#1A1C23] transition-colors ${!m.checked ? "bg-[#FF3B30]/[0.04]" : m.status === "fail" ? "bg-[#FF3B30]/[0.06]" : ""}`} data-testid={`compliance-row-${m.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-body font-700 truncate">{m.name}</p>
                  <p className="font-mono text-[11px] text-[#94A3B8] uppercase tracking-wide">{m.type}</p>
                </div>
                {m.checked && (
                  <div className="hidden sm:flex flex-col items-end text-right shrink-0 min-w-0">
                    <p className="font-body text-sm truncate max-w-[160px]">{m.operator}</p>
                    <p className="font-mono text-[11px] text-[#94A3B8] flex items-center gap-1"><Clock className="w-3 h-3" />{m.time}{m.count > 1 ? ` · ${m.count} checks` : ""}</p>
                  </div>
                )}
                <div className="shrink-0 w-28 text-right">{statusCell(m)}</div>
                <ChevronRight className="w-4 h-4 text-[#94A3B8] shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>

      {d.machines.some((m) => m.status === "fail" && m.failed_items.length > 0) && (
        <div className="card p-5 mt-4 border-l-4 border-l-[#FF3B30]" data-testid="compliance-faults">
          <h3 className="font-head font-800 uppercase tracking-tight text-lg mb-3 text-[#FF3B30]">Reported Faults</h3>
          <div className="space-y-2">
            {d.machines.filter((m) => m.status === "fail" && m.failed_items.length > 0).map((m) => (
              <div key={m.id} className="font-mono text-sm border-b border-white/5 pb-2">
                <span className="text-white">{m.name}</span>
                <span className="text-[#FF3B30]"> — {m.failed_items.join(", ")}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
